import { useState, useEffect } from 'react';

/**
 * Returns a live-updating formatted elapsed time string from a start timestamp.
 * Updates every second while the component is mounted.
 * Returns null if startTime is null/undefined.
 *
 * Format: "2h 14m 07s" while < 24h, "2d 3h" for longer durations.
 */
export function useElapsedTime(startTime: string | null | undefined): string | null {
  const [, forceUpdate] = useState(0);

  useEffect(() => {
    if (!startTime) return;
    const interval = setInterval(() => forceUpdate(n => n + 1), 1000);
    return () => clearInterval(interval);
  }, [startTime]);

  if (!startTime) return null;

  const totalSeconds = Math.max(0, Math.floor((Date.now() - new Date(startTime).getTime()) / 1000));
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
  }
  return `${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
}
